import { FC, SyntheticEvent, useState } from 'react';
import { Product } from '../../..//types';
import { getProductImageUrl } from '../../../utils/productImage';
import {
  CloseModalButton,
  EnlargeImageButton,
  ImageContainer,
  ModalContent,
  StyledModal,
  ZoomInIcon,
  CloseModalIcon,
  SectionColumn,
  EnlargedImage,
  ImageUrlTextbox,
  CopyUrlButton,
  EnlargedImageContainer,
  BackdropStyles
} from './enlarge-image-modal.styles';
import noProductImage from '../../../images/no-product-image.png';
import { ProductImage } from '../edit-product/upload-image-modal.styles';

interface ImageModalProps {
  product: Product;
}

export const EnlargeImageModal: FC<ImageModalProps> = ({ product }) => {
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = useState<string>(getProductImageUrl(product));

  function handleMouseOver() {
    setIsHovering(!isImageModalOpen);
  }

  function hideZoomEditIcon() {
    setIsHovering(false);
  }

  function openEnlargedModal() {
    setIsImageModalOpen(true);
  }

  function closeEnlargedModal() {
    setIsImageModalOpen(false);
  }

  function handleImageError(event: SyntheticEvent<HTMLImageElement>) {
    setImageSrc(noProductImage);
    event.currentTarget.src = noProductImage;
  }

  function isCopyButtonDisabled() {
    return imageSrc === noProductImage;
  }

  function getImageUrlText(): string {
    return isCopyButtonDisabled() ? 'No image' : imageSrc;
  }

  async function copyImageUrl() {
    await navigator.clipboard.writeText(imageSrc);
  }

  return (
    <ImageContainer onMouseLeave={hideZoomEditIcon} id='product-enlarge-image-container'>
      <ProductImage
        id='product-enlarge-image-modal'
        src={imageSrc}
        onMouseOver={handleMouseOver}
        onError={handleImageError}
      />
      {isHovering && (
        <EnlargeImageButton
          disableRipple
          size='small'
          onClick={openEnlargedModal}
          id='product-enlarge-image-enlarge-button'
        >
          <ZoomInIcon id='product-enlarge-image-enlarge-icon' />
        </EnlargeImageButton>
      )}
      <StyledModal
        open={isImageModalOpen}
        onClose={closeEnlargedModal}
        BackdropProps={BackdropStyles}
        id='product-enlarge-image-modal-body'
      >
        <ModalContent>
          <SectionColumn>
            <CloseModalButton
              size='small'
              onClick={closeEnlargedModal}
              id='product-enlarge-image-close-button'
            >
              <CloseModalIcon />
            </CloseModalButton>
            <EnlargedImageContainer>
              <EnlargedImage src={imageSrc} id='product-enlarge-image-image' />
            </EnlargedImageContainer>
            <ImageUrlTextbox
              id='product-enlarge-image-url'
              value={getImageUrlText()}
              InputProps={{ readOnly: true, disabled: isCopyButtonDisabled() }}
              multiline
              rows={2}
            />
            <CopyUrlButton
              onClick={copyImageUrl}
              disabled={isCopyButtonDisabled()}
              id='product-enlarge-image-copy-url-button'
            >
              Copy image URL
            </CopyUrlButton>
          </SectionColumn>
        </ModalContent>
      </StyledModal>
    </ImageContainer>
  );
};
